import React, { useEffect, useRef, useState } from 'react';
import { get } from 'lodash';

import style from './style.module.css';
import Link from '../../../Link';
import Spinner from '../../../Spinner';
import AsyncGet from '../../../Materials/AsyncGet';
import FormGroup from '../../FormGroup';
import { getIcon } from '../../../../helpers/icons';
import useOutsideClick from '../../../../hooks/useOutSideClick';

const PAGE_SIZE = 100;
const SEARCH_DEBOUNCE_MS = 400;

// Multi-select variant of Dropdown/Searchable — same async/paginated/search
// backend contract, but the bound field is an array of ids instead of a
// single one. Selecting an item toggles it in/out of the array and keeps
// the list open so several can be picked in one go.
const SearchableMulti = (props) => {
    const {
        meta,
        input: { value, onChange, onBlur, name },
        label = false,
        required = false,
        disabled = false,
        prompt = "None selected",
        placeholder = "Type to search...",
        async = {},
        onSelect = () => { },
    } = props;

    const ids = Array.isArray(value) ? value : [];

    const [resolvedNames, setResolvedNames] = useState({});
    const [keyword, setKeyword] = useState("");
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [load, setLoad] = useState(false);
    const divRef = useRef(null);
    const pagingRef = useRef(false);

    const closeList = () => {
        setOpen(false);
        setKeyword("");
        onBlur();
    };

    useOutsideClick(divRef, closeList);

    // Resolve display name for a selected id from the currently fetched
    // page first, falling back to the individually-resolved cache (needed
    // once the id scrolls out of the fetched page, e.g. after a refresh).
    const nameFor = (id) => {
        const found = items.find((item) => get(item, 'id') === id);
        return found ? get(found, 'name') : resolvedNames[id];
    };

    const idsToResolve = ids.filter((id) => !nameFor(id));
    const unselectedItems = items.filter((item) => !ids.includes(get(item, 'id')));
    const selectedLabel = ids.length === 0 ? prompt : ids.map((id) => nameFor(id) || '...').join(', ');

    const fetchPage = (newOffset) => {
        pagingRef.current = true;
        if (newOffset === 0) {
            setItems([]);
        }
        setOffset(newOffset);
        setLoad(Date.now());
    };

    // Debounced server-side search while the list is open
    useEffect(() => {
        if (open === false) {
            return;
        }
        const timer = setTimeout(() => fetchPage(0), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [keyword]);

    const openList = () => {
        if (open === true || disabled === true) {
            return;
        }
        setOpen(true);
        setKeyword("");
        fetchPage(0);
    };

    const toggleItem = (item) => {
        const id = get(item, 'id', '');
        if (id === '') {
            return;
        }
        setResolvedNames((prev) => ({ ...prev, [id]: get(item, 'name', '') }));
        const newIds = ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id];
        onChange(newIds);
        onSelect(item, newIds);
        setKeyword("");
    };

    const removeId = (id) => onChange(ids.filter((v) => v !== id));

    const onScrollList = (event, fetching) => {
        const el = event.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 16;
        if (nearBottom && fetching === false && pagingRef.current === false && items.length < total) {
            fetchPage(items.length);
        }
    };

    // Resolve all not-yet-known selected ids' names in a single request,
    // instead of one request per id.
    const onResolveNamesFetch = ({ data }) => {
        const list = get(data, 'list', []);
        setResolvedNames((prev) => {
            const next = { ...prev };
            list.forEach((item) => {
                const itemId = get(item, 'id');
                if (itemId) {
                    next[itemId] = get(item, 'name', '');
                }
            });
            return next;
        });
    };

    const onPageFetch = ({ data }) => {
        pagingRef.current = false;
        const list = get(data, 'list', []);
        setItems((prev) => (offset === 0 ? list : [...prev, ...list]));
        if (offset === 0) {
            setTotal(get(data, 'total', 0));
        }
    };

    return (
        <div className={style.finder} ref={divRef}>
            <FormGroup
                meta={meta}
                label={label}
                value={selectedLabel}
                required={required}
                disabled={disabled}
                fieldName={name}
                fieldValue={ids.length > 0 ? ids.join(',') : ''}
            >
                <div className={style.wrap}>
                    <div className={style.tagsInput} onClick={openList}>
                        {ids.map((id) => (
                            <span className={style.tag} key={id}>
                                <span>{nameFor(id) || '...'}</span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        className={style.tagRemove}
                                        onClick={(e) => { e.stopPropagation(); removeId(id); }}
                                    >
                                        &times;
                                    </button>
                                )}
                            </span>
                        ))}
                        <input
                            placeholder={ids.length === 0 ? placeholder : ''}
                            value={open ? keyword : ''}
                            onFocus={openList}
                            onChange={(e) => setKeyword(e.target.value)}
                            disabled={disabled}
                        />
                    </div>
                    <span className={`${style.findIcon}${open ? ` ${style.searchIcon}` : ` ${style.triangeIcon}`}`}>
                        <i className={getIcon(open ? 'Search' : 'Triange')}></i>
                    </span>
                    {idsToResolve.length > 0 && (
                        <AsyncGet
                            {...async}
                            loadOnMount={idsToResolve.join(',')}
                            paramsData={{ ids: idsToResolve }}
                            permissionText={false}
                            onFetch={onResolveNamesFetch}
                        />
                    )}
                    <AsyncGet
                        {...async}
                        loadOnMount={load}
                        paramsData={{ keyword, limit: PAGE_SIZE, offset }}
                        permissionText={false}
                        onFetch={onPageFetch}
                        onError={() => { pagingRef.current = false; }}
                        render={({ fetching }) => open && (
                            <div className={style.dropdownOptions} onScroll={(event) => onScrollList(event, fetching)}>
                                {ids.length > 0 && (
                                    <Link className={style.item} onClick={() => onChange([])}>
                                        <span>Clear selection</span>
                                    </Link>
                                )}
                                {unselectedItems.map((item) => (
                                    <Link
                                        className={style.item}
                                        key={get(item, 'id')}
                                        onClick={() => toggleItem(item)}
                                    >
                                        <span className={style.checkMark}></span>
                                        <span>{get(item, 'name')}</span>
                                    </Link>
                                ))}
                                {fetching === true && <div className={`${style.item} ${style.loading}`}><Spinner /></div>}
                                {fetching === false && unselectedItems.length === 0 && (
                                    <div className={style.item}>No results were found</div>
                                )}
                            </div>
                        )}
                    />
                </div>
            </FormGroup>
        </div>
    )
}

export default SearchableMulti;
